import { getMessaging, getToken, isSupported as isMessagingSupported } from 'firebase/messaging';
import { getFcmServiceWorkerRegistration } from '../fcmServiceWorker';
import { getFirebaseApp, isPushRuntimeEligible } from '../firebaseClient';

const VAPID_KEY = 'BJM5hhy60YBdKW3QKdfXHue7qPFaTgAwmF2-XpB34GwsbbNRjqlxUxLy9ZOiFmlXdhurgIljxwpx5sM6i1YYKZg';

export const PUSH_STATUS = {
  OK: 'ok',
  UNSUPPORTED: 'unsupported',
  IN_APP_BROWSER: 'in_app_browser',
  IOS_NOT_INSTALLED: 'ios_not_installed',
  DENIED: 'denied',
  DISMISSED: 'dismissed',
  TOKEN_ERROR: 'token_error',
};

const getUserAgent = () => (
  typeof navigator === 'undefined' ? '' : String(navigator.userAgent || '')
);

export const isStandaloneMode = () => {
  if (typeof window === 'undefined') return false;
  return Boolean(
    window.navigator?.standalone === true
    || (
      typeof window.matchMedia === 'function'
      && window.matchMedia('(display-mode: standalone)').matches
    )
  );
};

export const getPushEnvironment = () => {
  const ua = getUserAgent();
  const uaLower = ua.toLowerCase();
  const isIOS = /iphone|ipad|ipod/i.test(ua);
  const isAndroid = /android/i.test(ua);
  const isInAppBrowser = /telegram|instagram|fbav|fban|vkclient|vkontakte|line\/|micromessenger|whatsapp|okhttp|wv\)/i.test(ua)
    || (isAndroid && /\bwv\b/i.test(ua));
  const isSafari = /safari/i.test(ua) && !/chrome|crios|android|firefox|fxios|yabrowser|yasearch|opera|opr/i.test(ua);
  const isChrome = /chrome|crios/i.test(ua) && !/edg|edge|yabrowser|yasearch|opera|opr|samsungbrowser|firefox|fxios/i.test(ua);

  return {
    ua,
    uaLower,
    isIOS,
    isAndroid,
    isInAppBrowser,
    isSafari,
    isChrome,
    isStandalone: isStandaloneMode(),
    notificationPermission: typeof Notification === 'undefined' ? 'unsupported' : Notification.permission,
  };
};

export const getPushInstruction = (status) => {
  const env = getPushEnvironment();

  if (status === PUSH_STATUS.IN_APP_BROWSER || env.isInAppBrowser) {
    return 'Откройте приложение во внешнем браузере: на iPhone через Safari, на Android через Chrome. Во встроенном браузере Telegram/VK/Instagram push-уведомления часто недоступны.';
  }

  if (status === PUSH_STATUS.IOS_NOT_INSTALLED) {
    return 'На iPhone уведомления работают только после установки PWA: откройте сайт в Safari, нажмите «Поделиться», выберите «На экран Домой», затем откройте Salew App через иконку.';
  }

  if (status === PUSH_STATUS.DENIED) {
    if (env.isIOS) {
      return 'Уведомления запрещены. Откройте «Настройки» iPhone → «Уведомления» → Salew App и включите разрешение. Если пункта нет, переустановите PWA через Safari.';
    }
    if (env.isAndroid) {
      return 'Уведомления запрещены. Откройте настройки сайта в Chrome: значок замка рядом с адресом → «Уведомления» → «Разрешить».';
    }
    return 'Уведомления запрещены в браузере. Нажмите на значок замка возле адреса сайта и разрешите уведомления.';
  }

  if (status === PUSH_STATUS.DISMISSED) {
    return 'Разрешение на уведомления не выдано. Нажмите «Включить уведомления» еще раз и выберите «Разрешить».';
  }

  if (status === PUSH_STATUS.TOKEN_ERROR) {
    return 'Не удалось получить токен уведомлений. Перезапустите приложение и попробуйте снова. Если не поможет, напишите в поддержку.';
  }

  return 'Этот браузер или режим запуска не поддерживает push-уведомления. Используйте установленное PWA в Safari на iPhone или Chrome на Android.';
};

const checkBasePushSupport = async () => {
  const env = getPushEnvironment();

  if (env.isInAppBrowser) {
    return { ok: false, status: PUSH_STATUS.IN_APP_BROWSER, env };
  }
  if (env.isIOS && !env.isStandalone) {
    return { ok: false, status: PUSH_STATUS.IOS_NOT_INSTALLED, env };
  }
  if (!isPushRuntimeEligible()) {
    return { ok: false, status: PUSH_STATUS.UNSUPPORTED, env };
  }

  const supported = await isMessagingSupported().catch(() => false);
  if (!supported) {
    return { ok: false, status: PUSH_STATUS.UNSUPPORTED, env };
  }

  return { ok: true, status: PUSH_STATUS.OK, env };
};

const getFcmToken = async () => {
  const swRegistration = await getFcmServiceWorkerRegistration();
  const messaging = getMessaging(getFirebaseApp());
  return await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: swRegistration,
  });
};

export const refreshPushTokenIfGranted = async () => {
  const support = await checkBasePushSupport();
  if (!support.ok) return { ...support, token: null };

  if (Notification.permission !== 'granted') {
    return {
      ok: false,
      status: Notification.permission === 'denied' ? PUSH_STATUS.DENIED : PUSH_STATUS.DISMISSED,
      env: support.env,
      token: null,
    };
  }

  try {
    const token = await getFcmToken();
    return token
      ? { ok: true, status: PUSH_STATUS.OK, env: support.env, token }
      : { ok: false, status: PUSH_STATUS.TOKEN_ERROR, env: support.env, token: null };
  } catch (error) {
    return { ok: false, status: PUSH_STATUS.TOKEN_ERROR, env: support.env, token: null, error };
  }
};

export const requestPushToken = async () => {
  const support = await checkBasePushSupport();
  if (!support.ok) return { ...support, token: null };

  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }

  if (permission !== 'granted') {
    return {
      ok: false,
      status: permission === 'denied' ? PUSH_STATUS.DENIED : PUSH_STATUS.DISMISSED,
      env: support.env,
      token: null,
    };
  }

  try {
    const token = await getFcmToken();
    return token
      ? { ok: true, status: PUSH_STATUS.OK, env: support.env, token }
      : { ok: false, status: PUSH_STATUS.TOKEN_ERROR, env: support.env, token: null };
  } catch (error) {
    return { ok: false, status: PUSH_STATUS.TOKEN_ERROR, env: support.env, token: null, error };
  }
};
